import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../authContext/context';
import { postRequest } from '../../../lib/api';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center">Searching...</td></tr>
                ) : searchResults.length === 0 ? (
                  <tr><td colSpan="5" className="text-center">No customers found</td></tr>
                ) : (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title || '-'}</td>
                      <td>{customer.forename} {customer.surname}</td>
                      <td>{customer.email || '-'}</td>
                      <td>{customer.phone_number || '-'}</td>
                      <td>
                        {isCustomerAdded(customer._id) ? (
                          <span className="badge bg-success">Added</span>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              onCustomerAdd(customer);
                              onHide();
                            }}
                          >
                            Add
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function EditFirstRegistration() {
  const navigate = useNavigate();
  const { lead_id } = useParams();
  const authCtx = useContext(AuthContext);

  const [leadData, setLeadData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: {
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      property_value: ''
    }
  });

  // Fetch lead data
  const fetchLeadData = async () => {
    console.log('🔍 === FETCHING FIRST REGISTRATION LEAD DATA ===');
    console.log('📍 Lead ID:', lead_id);

    try {
      setLoading(true);

      // Fetch lead data
      const response = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);
      console.log('📡 Full API response:', response);

      if ((response?.status === 'success' || response?.status === 200) && response?.data) {
        const lead = response.data;
        console.log('✅ Lead data fetched:', lead);
        console.log('🔍 propertyDetails:', lead.propertyDetails);
        console.log('🔍 property_address:', lead.property_address);
        console.log('🔍 property_value:', lead.property_value);

        setLeadData(lead);

        // Auto-fill property address from propertyDetails
        if (lead.propertyDetails?.address) {
          const address = lead.propertyDetails.address;
          console.log('✅ Using propertyDetails.address:', address);
          setValue('line1', address.line1 || '');
          setValue('line2', address.line2 || '');
          setValue('town', address.town || '');
          setValue('county', address.county || '');
          setValue('postcode', address.postcode || '');
        } else if (lead.property_address) {
          console.log('⚠️ propertyDetails.address not found, using property_address fallback');
          // Fallback to property_address if propertyDetails doesn't exist
          const address = lead.property_address;
          if (typeof address === 'object') {
            setValue('line1', address.line1 || address.address1 || '');
            setValue('line2', address.line2 || address.address2 || '');
            setValue('town', address.town || '');
            setValue('county', address.county || '');
            setValue('postcode', address.postcode || '');
          }
        }

        // Auto-fill property value from propertyDetails
        if (lead.propertyDetails?.propertyValue) {
          setValue('property_value', lead.propertyDetails.propertyValue);
        } else {
          setValue('property_value', lead.property_value || lead.net_value || '');
        }

        // Auto-fill customers
        if (lead.customers && Array.isArray(lead.customers) && lead.customers.length > 0) {
          const firstCustomer = lead.customers[0];

          if (typeof firstCustomer === 'object' && firstCustomer._id) {
            // Already populated
            setCustomers(lead.customers);
            console.log('✅ Customers auto-filled:', lead.customers.length);
          } else if (typeof firstCustomer === 'string') {
            // Need to fetch customer details
            console.log('🔍 Fetching customer details for IDs:', lead.customers);
            try {
              const customerPromises = lead.customers.map(async (customerId) => {
                try {
                  const customerRes = await postRequest(`private/getCustomerIndividualData/${customerId}`, {}, authCtx.token);
                  if (customerRes?.data && customerRes.data._id) {
                    console.log('✅ Customer fetched:', customerRes.data._id, customerRes.data.forename, customerRes.data.surname);
                    return customerRes.data;
                  }
                  console.warn('⚠️ Invalid customer data for ID:', customerId);
                  return null;
                } catch (err) {
                  console.warn('⚠️ Customer not found or error fetching customer:', customerId, err.message || err);
                  return null;
                }
              });
              const fetchedCustomers = await Promise.all(customerPromises);
              const validCustomers = fetchedCustomers.filter(c => c && c._id);
              setCustomers(validCustomers);
              console.log(`✅ ${validCustomers.length} valid customers out of ${lead.customers.length} fetched`);

              // If some customers couldn't be fetched, show a warning
              if (validCustomers.length < lead.customers.length) {
                console.warn(`⚠️ ${lead.customers.length - validCustomers.length} customer(s) could not be loaded (may have been deleted)`);
              }
            } catch (err) {
              console.error('❌ Error in customer fetching:', err);
            }
          }
        }
      } else {
        throw new Error('Failed to fetch lead data');
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch lead data'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lead_id && authCtx.token) {
      fetchLeadData();
    }
  }, [lead_id, authCtx.token]);

  const handleAddCustomer = (customer) => {
    if (!customers.some(c => c._id === customer._id)) {
      setCustomers([...customers, customer]);
    }
  };

  const handleRemoveCustomer = (customerId) => {
    setCustomers(customers.filter(c => c._id !== customerId));
  };

  const onSubmit = async (formData) => {
    try {
      if (customers.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Missing Information',
          text: 'Please add at least one customer'
        });
        return;
      }

      const payload = {
        conveyancing_id: lead_id,
        customers: customers.map(c => c._id),
        propertyDetails: {
          address: {
            line1: formData.line1,
            line2: formData.line2,
            town: formData.town,
            county: formData.county,
            postcode: formData.postcode
          },
          propertyValue: parseFloat(formData.property_value) || 0
        }
      };

      console.log('💾 Saving First Registration data:', payload);

      const res = await postRequest('private/updateConveyancingFirstRegistration', payload, authCtx.token);

      if (res?.status === 'success' || res?.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'First Registration details updated successfully!'
        }).then(() => {
          navigate(`/leads/view/first-registration/${lead_id}`);
        });
      } else {
        throw new Error(res?.message || 'Failed to update details');
      }
    } catch (error) {
      console.error('Error updating details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update details'
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit First Registration', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            Edit First Registration - {leadData?.lead_id || lead_id}
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Property Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Property Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Address</Typography>
                  <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Address Line 1"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Address Line 2"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="town"
                        control={control}
                        rules={{ required: 'Town is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Town"
                            error={!!errors.town}
                            helperText={errors.town?.message}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="county"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="County"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="postcode"
                        control={control}
                        rules={{ required: 'Postcode is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Postcode"
                            error={!!errors.postcode}
                            helperText={errors.postcode?.message}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="property_value"
                    control={control}
                    rules={{
                      required: 'Property value is required',
                      min: { value: 0, message: 'Property value must be positive' }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Property Value"
                        type="number"
                        error={!!errors.property_value}
                        helperText={errors.property_value?.message}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Customers Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#666' }}>
                  Customers
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCustomerModal(true)}
                  size="small"
                >
                  Add Customer
                </Button>
              </Box>

              {customers.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body2" color="text.secondary">
                    No customers added yet. Click "Add Customer" to add.
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {customers.map((customer) => (
                    <Box
                      key={customer._id}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {`${customer.title || ''} ${customer.forename || ''} ${customer.surname || ''}`.trim()}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          {customer.email && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1976d2' }}>
                              ✉ {customer.email}
                            </Typography>
                          )}
                          {customer.phone_number && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
                              📞 {customer.phone_number}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <CloseIcon
                        onClick={() => handleRemoveCustomer(customer._id)}
                        sx={{
                          cursor: 'pointer',
                          color: '#d32f2f',
                          fontSize: '20px',
                          '&:hover': { color: '#b71c1c' }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="error"
                size="large"
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate(`/leads/view/first-registration/${lead_id}`)}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddCustomer}
        selectedCustomers={customers}
      />
    </>
  );
}
